import React, { useState, useRef, useEffect } from "react";
import {
    Fab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    IconButton,
    Box,
    Alert,
    Collapse,
    Paper,
    List,
    ListItem,
    Divider
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { t } from "i18next";
import AiSuggestionsItemCard from "../sections/aiSuggestionsItemCard";
import SuggestionMainItemInfo from "../Item/suggestionMainItemInfo";
import {
    getAuth,
    getLocalStorageMenuItem,
    setMobileBackColor,
    setMobileCardBorderColor,
    setMobileTextColor,
    setMobileTextField_BK_FONT_Border_StyleColor,
} from "../../../utility";
import helpMessageIconDark from "../../../images/Featured icon.png";
import helpMessageIconLight from "../../../images/Featured icon-1.png";

const FloatingChatButton = ({
    qrCode,
    qrName,
    sympole,
    handleFavorite,
    favorites,
    suggested,
    onActionDone,
    treeData
}) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [suggestedItems, setSuggestedItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [userQuery, setUserQuery] = useState("");
    const [responseDescription, setResponseDescription] = useState("");
    const [selectedItem, setSelectedItem] = useState(null);
    const [isItemDialogOpen, setIsItemDialogOpen] = useState(false);
    const [isNoDataDialogOpen, setIsNoDataDialogOpen] = useState(false);
    const [showWarningAlert, setShowWarningAlert] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const messagesEndRef = useRef(null);

    const theme = getLocalStorageMenuItem("theme");
    const lang = getLocalStorageMenuItem("lang");
    const [searchParams] = useSearchParams();

    const isRTL = lang === "ar" || lang === "fa";
    const textDirection = isRTL ? 'rtl' : 'ltr';
    const textAlignment = isRTL ? 'right' : 'left';
    const fontFamily = isRTL ? "Almarai" : "Roboto";

    const dialogStyles = {
        backgroundColor: setMobileTextField_BK_FONT_Border_StyleColor()[0],
        color: setMobileTextField_BK_FONT_Border_StyleColor()[1],
        borderColor: setMobileTextField_BK_FONT_Border_StyleColor()[2],
    };

    const textFieldStyles = {
        direction: textDirection,
        textAlign: textAlignment,
        borderRadius: '8px',
        backgroundColor: setMobileBackColor(),
        color: setMobileTextColor(),
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: dialogStyles.borderColor,
        fontFamily,
    };

    // Scroll to bottom of chat when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatHistory, suggestedItems]);

    const openDialog = () => {
        setIsDialogOpen(true);
        // Add welcome message when dialog opens
        setChatHistory([{
            type: 'bot',
            content: t('Hi') + ` ${getLocalStorageMenuItem("mobileLoginToken") ? getLocalStorageMenuItem("customer_name") : ''}! ` + t('How can I help you with the menu today?')
        }]);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSuggestedItems([]);
        setUserQuery("");
        setResponseDescription("");
        setShowWarningAlert(false);
        setChatHistory([]);
    };

    const fetchAISuggestions = async () => {
        if (!userQuery.trim()) return;

        // Add user message to chat history
        setChatHistory(prev => [...prev, {
            type: 'user',
            content: userQuery
        }]);

        setSuggestedItems([]);
        setResponseDescription("");
        setIsLoading(true);
        setShowWarningAlert(false);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API}/menus/${qrCode}/recommend-items-with-ai-2`,
                { prompt: userQuery },
                getAuth()
            );

            const items = response?.data?.data?.items;
            const description = response?.data?.data?.description;

            setResponseDescription(description);

            if (items?.length > 0 || description) {
                if (items?.length > 0) {
                    setSuggestedItems(items);
                    setShowWarningAlert(true);
                }

                // Add bot response to chat history
                setChatHistory(prev => [...prev, {
                    type: 'bot',
                    content: description || t("Here are some suggestions based on your query:")
                }]);
            } else {
                setIsNoDataDialogOpen(true);
                // Add no results message to chat history
                setChatHistory(prev => [...prev, {
                    type: 'bot',
                    content: t("I couldn't find any items matching your request. Please try a different query.")
                }]);
            }
        } catch (error) {
            console.error("Error fetching AI suggestions:", error);
            // Add error message to chat history
            setChatHistory(prev => [...prev, {
                type: 'bot',
                content: t("Sorry, I encountered an error. Please try again later.")
            }]);
        } finally {
            setIsLoading(false);
            setUserQuery("");
        }
    };

    const openItemDetails = (item) => {
        setSelectedItem(item);
        setIsItemDialogOpen(true);
    };

    const closeItemDetails = () => {
        setIsItemDialogOpen(false);
        setSelectedItem(null);
    };

    const closeNoDataDialog = () => setIsNoDataDialogOpen(false);
    const closeWarningAlert = () => setShowWarningAlert(false);

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            fetchAISuggestions();
        }
    };

    return (
        <>
            {/* Floating Action Button */}
            <Fab
                color="primary"
                aria-label="AI assistant"
                onClick={openDialog}
                style={{
                    position: 'fixed',
                    bottom: !treeData?.details?.order_ability && !treeData?.details?.whatsapp ? '10px' : '90px',
                    right: '16px',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                    backgroundColor: setMobileBackColor(),
                }}
            >
                <img
                    src={theme === "dark" ? helpMessageIconDark : helpMessageIconLight}
                    alt="AI Assistant"
                    style={{ width: "60px", height: "60px" }}
                />
            </Fab>

            {/* No Data Found Dialog */}
            <Dialog
                open={isNoDataDialogOpen}
                onClose={closeNoDataDialog}
                maxWidth="xs"
                PaperProps={{
                    style: {
                        borderRadius: 15,
                        padding: "20px",
                        backgroundColor: dialogStyles.backgroundColor
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", paddingBottom: 0 }}>
                    <Typography variant="h5" fontWeight="bold" color={dialogStyles.color}>
                        {t("No Matching Data")}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "20px 10px" }}>
                        <Typography variant="body1" textAlign="center" color={dialogStyles.color}>
                            {t("No data matching your search.")}
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center" }}>
                    <Button
                        onClick={closeNoDataDialog}
                        sx={{
                            backgroundColor: dialogStyles.color,
                            color: dialogStyles.backgroundColor,
                            padding: "10px 20px",
                            borderRadius: "30px",
                            "&:hover": {
                                backgroundColor: "primary.dark",
                            },
                        }}
                    >
                        {t("Close")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* AI Warning Alert Dialog */}
            <Dialog
                open={showWarningAlert}
                onClose={closeWarningAlert}
                maxWidth="sm"
                PaperProps={{
                    style: {
                        borderRadius: 15,
                        padding: "20px",
                        backgroundColor: dialogStyles.backgroundColor,
                        border: `1px solid ${theme === 'dark' ? '#ff9800' : '#f57c00'}`,
                    },
                }}
            >
                <DialogTitle sx={{ textAlign: "center", paddingBottom: 0 }}>
                    <Typography variant="h6" fontWeight="bold" color={theme === 'dark' ? '#ff9800' : '#f57c00'}>
                        {t("Important Note")}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: "flex", justifyContent: "center", padding: "10px" }}>
                        <Typography variant="body1" textAlign="center" color={dialogStyles.color}>
                            {t("These items are suggested by AI. Please take care if you have any allergies or special requirements.")}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ justifyContent: "center" }}>
                    <Button
                        onClick={closeWarningAlert}
                        sx={{
                            backgroundColor: theme === 'dark' ? '#ff9800' : '#f57c00',
                            color: '#fff',
                            padding: "8px 16px",
                            borderRadius: "30px",
                            "&:hover": {
                                backgroundColor: theme === 'dark' ? '#f57c00' : '#e65100',
                            },
                        }}
                    >
                        {t("I Understand")}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Main AI Assistant Dialog */}
            <Dialog
                className="alignByLang"
                open={isDialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="sm"
                fullScreen
                PaperProps={{
                    style: {
                        borderRadius: 0,
                        ...dialogStyles,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    },
                }}
            >
                <DialogTitle style={{
                    padding: '16px',
                    borderBottom: `1px solid ${dialogStyles.borderColor}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }} className="alignByLang">

                    <Typography variant="h6" style={{ fontWeight: 'bold', color: setMobileTextColor() }}>
                        {t("Menu Assistant")}
                    </Typography>

                    <IconButton onClick={closeDialog}>
                        <CloseIcon style={{ color: setMobileTextColor() }} />
                    </IconButton>

                </DialogTitle>

                <DialogContent style={{
                    flex: 1,
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
                    className="alignByLang"
                >
                    {/* Chat History */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '16px',
                    }}>
                        <List>
                            {chatHistory.map((message, index) => (
                                <React.Fragment key={index}>
                                    <ListItem
                                        className="alignByLang"
                                        style={{
                                            justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                                            padding: '8px 0',
                                        }}
                                    >
                                        <Paper
                                            className="alignByLang"
                                            elevation={0}
                                            style={{
                                                padding: '12px 16px',
                                                maxWidth: '80%',
                                                backgroundColor: message.type === 'user'
                                                    ? setMobileBackColor()
                                                    : setMobileCardBorderColor(),
                                                color: message.type === 'user'
                                                    ? setMobileTextColor()
                                                    : setMobileBackColor(),
                                                borderRadius: message.type === 'user'
                                                    ? !isRTL
                                                        ? '18px 18px 0 18px'
                                                        : '18px 18px 18px 0'
                                                    : !isRTL
                                                        ? '18px 18px 18px 0'
                                                        : '18px 18px 0 18px',
                                            }}
                                        >
                                            <Typography style={{ fontFamily }}>
                                                {message.content}
                                            </Typography>
                                        </Paper>
                                    </ListItem>
                                    {index < chatHistory.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}

                            {/* Loading indicator */}
                            {isLoading && (
                                <ListItem style={{ justifyContent: 'flex-start', padding: '8px 0' }}>
                                    <Paper
                                        elevation={0}
                                        style={{
                                            padding: '12px 16px',
                                            maxWidth: '80%',
                                            backgroundColor: setMobileCardBorderColor(),
                                            color: setMobileBackColor(),
                                            borderRadius:
                                                !isRTL
                                                    ? '18px 18px 18px 0'
                                                    : '18px 18px 0 18px',
                                        }}
                                    >
                                        <Typography style={{ fontFamily }}>
                                            {t("Thinking...")}
                                        </Typography>
                                    </Paper>
                                </ListItem>
                            )}

                            {/* Suggested Items */}
                            {suggestedItems?.length > 0 && (
                                <>
                                    <Divider />
                                    <ListItem style={{
                                        justifyContent: 'flex-start',
                                        padding: '8px 0',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start'
                                    }}>
                                        <Typography
                                            variant="subtitle2"
                                            style={{
                                                marginBottom: '8px',
                                                color: setMobileTextColor(),
                                                fontFamily,
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {t("Suggested items")}:
                                        </Typography>
                                        <div style={{
                                            width: '100%',
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                            gap: '12px'
                                        }}>
                                            {suggestedItems.map((item) => (
                                                <AiSuggestionsItemCard
                                                    key={item.id}
                                                    item={item}
                                                    in_root={false}
                                                    qrCode={qrCode}
                                                    qrName={qrName}
                                                    sympole={sympole || ""}
                                                    handleFavorite={handleFavorite}
                                                    favorites={favorites}
                                                    suggested={suggested}
                                                    handleOpenItemDialog={openItemDetails}
                                                    style={{ margin: '4px 0' }}
                                                />
                                            ))}
                                        </div>
                                    </ListItem>
                                </>
                            )}
                            <div ref={messagesEndRef} />
                        </List>
                    </div>

                    {/* Input Area - Fixed at bottom */}
                    <div className="alignByLang" style={{
                        padding: '16px',
                        borderTop: `1px solid ${dialogStyles.borderColor}`,
                        backgroundColor: dialogStyles.backgroundColor
                    }}>
                        <Box className="alignByLang" display="flex" alignItems="center" gap={1}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder={t("Ask about menu items...")}
                                value={userQuery}
                                onChange={(e) => setUserQuery(e.target.value)}
                                onKeyPress={handleKeyPress}
                                multiline
                                maxRows={4}
                                InputProps={{
                                    style: {
                                        ...textFieldStyles,
                                        borderRadius: '24px',
                                        paddingRight: '12px'
                                    },
                                }}
                            />

                            <IconButton
                                color="primary"
                                onClick={fetchAISuggestions}
                                disabled={!userQuery.trim() || isLoading}
                                style={{
                                    backgroundColor: theme === 'dark' ? '#1976d2' : '#bbdefb',
                                    color: theme === 'dark' ? '#fff' : '#0d47a1',
                                    transform: isRTL ? 'scaleX(-1)' : 'none'
                                }}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                        <Typography
                            variant="caption"
                            display="block"
                            style={{
                                marginTop: '4px',
                                color: setMobileTextColor(),
                                opacity: 0.7,
                                textAlign: 'center',
                                fontFamily
                            }}
                        >
                            {t("AI-powered suggestions. Results may vary.")}
                        </Typography>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Item Details Dialog */}
            {selectedItem && (
                <Dialog
                    open={isItemDialogOpen}
                    onClose={closeItemDetails}
                    fullWidth
                    fullScreen
                    maxWidth="sm"
                    PaperProps={{
                        style: {
                            height: "100%",
                            ...dialogStyles
                        },
                    }}
                >
                    <SuggestionMainItemInfo
                        item={selectedItem}
                        sympoleData={sympole}
                        handleFavorite={handleFavorite}
                        favorites={favorites}
                        tableQr={qrCode}
                        tableName={qrName}
                        handleCloseItemDialog={closeItemDetails}
                        onActionDone={onActionDone}
                        treeData={treeData}
                    />
                </Dialog>
            )}
        </>
    );
};

export default FloatingChatButton;